import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * The one "are you sure?" dialog for the whole app, in place of the
 * browser's confirm box. Mounted once (`ConfirmProvider` in App.js) and
 * asked from anywhere:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete this document?', description: '…',
 *                         confirmLabel: 'Delete', destructive: true }))) return;
 *
 * Pass `action` (an async function) to run the work from inside the dialog:
 * the confirm button shows busy until it finishes, the dialog closes on
 * success and stays open (resolving false) on failure, so the caller
 * doesn't need its own busy state. Errors from `action` are rethrown.
 *
 *   await confirm({ …, action: () => documentsApi.delete(id) });
 */
const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null); // {opts, resolve, reject}
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const ask = useCallback((opts) => new Promise((resolve, reject) => {
    setRequest({ opts: opts || {}, resolve, reject });
  }), []);

  const close = (value) => {
    const r = request;
    setRequest(null);
    setBusy(false);
    busyRef.current = false;
    r?.resolve(value);
  };

  const onConfirm = async (e) => {
    e?.preventDefault();
    if (!request || busyRef.current) return;
    if (!request.opts.action) {
      close(true);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      await request.opts.action();
    } catch (err) {
      const r = request;
      setRequest(null);
      setBusy(false);
      busyRef.current = false;
      r.reject(err);
      return;
    }
    close(true);
  };

  const value = useMemo(() => ask, [ask]);
  const opts = request?.opts || {};

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog open={!!request} onOpenChange={(o) => { if (!o && !busyRef.current) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              {opts.title || 'Are you sure?'}
            </AlertDialogTitle>
            {(opts.detail || opts.description) && (
              <AlertDialogDescription className="text-ink-70">
                {opts.detail && <span className="block font-medium text-ink mb-2 break-words">{opts.detail}</span>}
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="confirm-cancel">{opts.cancelLabel || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirm}
              disabled={busy}
              className={opts.destructive ? 'bg-breach-wash text-breach hover:bg-[#F6D2CA]' : undefined}
              data-testid="confirm-accept"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {opts.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

/** The ask function; see ConfirmProvider. Falls back to the browser's
 *  confirm when rendered outside the provider (tests, isolated mounts). */
export const useConfirm = () => {
  const ask = useContext(ConfirmContext);
  return ask || (async (opts) => window.confirm([opts?.title, opts?.description].filter(Boolean).join('\n\n')));
};

export default ConfirmProvider;
